((root, factory) => {
  const ruleEngine = typeof module === 'object' && module.exports
    ? require('./corkopia-rule-engine.js')
    : root.CorkopiaRuleEngine;
  const api = factory(root, ruleEngine);
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.CorkopiaDynamicInventory = api;
})(typeof globalThis !== 'undefined' ? globalThis : window, (root, ruleEngine) => {
  'use strict';

  const CONTRACT_VERSION = 'dynamic-inventory-v1.0.0';
  const RULE_SET_ID = 'dynamic_inventory_v1';
  const RULE_SET_VERSION = '1.0.0';
  const PROVIDER_STATUS = 'shopify_public_only';
  const C3_PROVIDER_STATUS = 'not_connected';
  const EVENT_CHANNEL = 'corkopia:inventory';
  const MERCHANDISING_PRIORITIES = Object.freeze([
    'featured',
    'newest',
    'rare',
    'statement',
    'wave',
    'owner_pick',
    'seasonal',
    'trending',
    'recently_released'
  ]);
  const FUTURE_C3_STATES = Object.freeze([
    'LIVE',
    'DRAFT',
    'APPROVED',
    'READY',
    'WAVE',
    'RESERVED',
    'PUBLISHING',
    'SOLD',
    'REPLACEMENT_CANDIDATE'
  ]);
  const EVENT_NAMES = Object.freeze({
    published: 'inventory.published',
    removed: 'inventory.removed',
    railRefreshed: 'inventory.rail_refreshed',
    recommendationRefreshed: 'inventory.recommendation_refreshed',
    waveChanged: 'inventory.wave_changed',
    replacementAvailable: 'inventory.replacement_available',
    depleted: 'inventory.depleted',
    familyImbalance: 'inventory.family_imbalance'
  });
  const REPLACEMENT_REASONS = Object.freeze([
    'REPLACE_SOLD_PIECE',
    'REPLACE_MISSING_HERO',
    'REPLACE_EMPTY_RAIL',
    'REPLACE_MISSING_STATEMENT_PIECE'
  ]);
  const SURFACES = Object.freeze([
    'home_rail',
    'collection',
    'search',
    'recommendation',
    'build_studio',
    'smart_box',
    'future_ai'
  ]);

  const text = (value) => value === null || value === undefined ? '' : String(value);
  const list = (value) => Array.isArray(value) ? value.filter(Boolean) : [];
  const unique = (values) => [...new Set(values.filter(Boolean))];
  const publicId = (value) => text(value).replace(/[^a-zA-Z0-9:_-]/g, '').slice(0, 160);
  const normalizedToken = (value) => text(value).trim().toLowerCase().replace(/[\s-]+/g, '_');
  const parseBoundMap = (value) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return Object.fromEntries(Object.entries(value)
        .map(([family, count]) => [normalizedToken(family), Number(count)])
        .filter(([family, count]) => family && Number.isInteger(count) && count >= 0));
    }
    return Object.fromEntries(text(value).split(',')
      .map((entry) => entry.split(':').map((part) => part.trim()))
      .map(([family, count]) => [normalizedToken(family), Number(count)])
      .filter(([family, count]) => family && Number.isInteger(count) && count >= 0));
  };
  const variantIdFor = (product) => text(
    product?.commerce?.selectedVariant?.id || product?.identity?.shopifyVariantId
  );
  const productIdFor = (product) => text(product?.identity?.shopifyProductId);
  const pieceIdFor = (product) => text(product?.identity?.pieceId);
  const identityKey = (product) => [
    productIdFor(product),
    variantIdFor(product),
    pieceIdFor(product)
  ].join(':');
  const familyFor = (product) => normalizedToken(product?.classification?.family);
  const timestampFor = (product, priority) => {
    const value = priority === 'recently_released'
      ? product?.release?.releasedAt || product?.classification?.arrivalDate || product?.publication?.publishedAt
      : product?.classification?.arrivalDate || product?.publication?.publishedAt || product?.publication?.createdAt;
    const timestamp = Date.parse(value || '');
    return Number.isFinite(timestamp) ? timestamp : 0;
  };
  const labelsFor = (product) => unique([
    ...list(product?.merchandising?.recommendationLabels),
    ...list(product?.merchandising?.badges),
    ...list(product?.release?.labels),
    product?.classification?.statementPiece ? 'statement' : '',
    product?.classification?.newArrival ? 'newest' : ''
  ].map(normalizedToken));

  const contractFor = (surface) => Object.freeze({
    contract_version: CONTRACT_VERSION,
    service_id: 'corkopia_dynamic_inventory',
    surface: SURFACES.includes(surface) ? surface : 'recommendation',
    current_provider: 'shopify_public_sections',
    current_provider_status: PROVIDER_STATUS,
    c3_provider_status: C3_PROVIDER_STATUS,
    eligibility_rule_set: RULE_SET_ID,
    shopify_authoritative_for_sellability: true,
    future_inventory_payload_allowed: false,
    pagination_required: true
  });

  const normalizeQuery = (query = {}) => {
    const requestedLimit = Number(query.limit);
    const candidatePageSize = Number(query.candidatePageSize);
    return {
      request_id: publicId(query.request_id || query.requestId),
      surface: SURFACES.includes(query.surface) ? query.surface : 'recommendation',
      surface_id: publicId(query.surface_id || query.surfaceId),
      section_id: publicId(query.section_id || query.sectionId),
      source_url: text(query.source_url || query.sourceUrl),
      family: normalizedToken(query.family),
      priorities: unique(list(query.priorities).map(normalizedToken))
        .filter((priority) => MERCHANDISING_PRIORITIES.includes(priority)),
      limit: Number.isInteger(requestedLimit) && requestedLimit > 0 ? requestedLimit : null,
      candidate_page_size: Number.isInteger(candidatePageSize) && candidatePageSize > 0
        ? candidatePageSize
        : null,
      balance: {
        enabled: query.balance?.enabled === true,
        family_order: unique(list(query.balance?.family_order).map(normalizedToken)),
        minimum_by_family: parseBoundMap(query.balance?.minimum_by_family),
        maximum_by_family: parseBoundMap(query.balance?.maximum_by_family)
      },
      exclusions: {
        product_ids: new Set(list(query.exclusions?.product_ids).map(text)),
        variant_ids: new Set(list(query.exclusions?.variant_ids).map(text)),
        piece_ids: new Set(list(query.exclusions?.piece_ids).map(text))
      },
      public_fields_only: true,
      current_eligible_only: true
    };
  };

  const evaluateEligibility = (products, engine = ruleEngine) => {
    const subjects = list(products);
    const response = engine?.evaluate?.({
      evaluation_id: `inventory-${subjects.map(identityKey).join('|')}`,
      rule_set_id: RULE_SET_ID,
      rule_set_version: RULE_SET_VERSION,
      subjects,
      facts: {}
    }) || {
      results: [],
      hard_failures: ['RULE_ENGINE_UNAVAILABLE'],
      unknown_required_facts: ['RULE_ENGINE_UNAVAILABLE']
    };
    const decisions = subjects.map((product) => {
      const variantId = variantIdFor(product);
      const results = response.results.filter((entry) =>
        text(entry.subject?.variant_id) === variantId
      );
      const failures = results.filter((entry) =>
        entry.enforcement === 'HARD' && ['FAIL', 'UNKNOWN'].includes(entry.outcome)
      );
      return {
        subject: {
          product_id: productIdFor(product),
          variant_id: variantId,
          piece_id: pieceIdFor(product)
        },
        product,
        eligible: results.length > 0 && failures.length === 0,
        reason_codes: failures.length
          ? failures.map((entry) => entry.reason_code)
          : results.map((entry) => entry.reason_code),
        rule_results: results
      };
    });
    return {
      rule_set_id: RULE_SET_ID,
      rule_set_version: RULE_SET_VERSION,
      decisions,
      hard_failures: response.hard_failures || [],
      unknown_required_facts: response.unknown_required_facts || []
    };
  };

  const excludedByQuery = (product, query) =>
    query.exclusions.product_ids.has(productIdFor(product))
    || query.exclusions.variant_ids.has(variantIdFor(product))
    || query.exclusions.piece_ids.has(pieceIdFor(product));

  const deduplicate = (products) => {
    const seenProducts = new Set();
    const seenVariants = new Set();
    const seenPieces = new Set();
    const accepted = [];
    const duplicates = [];
    list(products).forEach((product) => {
      const productId = productIdFor(product);
      const variantId = variantIdFor(product);
      const pieceId = pieceIdFor(product);
      const duplicate = (productId && seenProducts.has(productId))
        || (variantId && seenVariants.has(variantId))
        || (pieceId && seenPieces.has(pieceId));
      if (duplicate) {
        duplicates.push(identityKey(product));
        return;
      }
      if (productId) seenProducts.add(productId);
      if (variantId) seenVariants.add(variantId);
      if (pieceId) seenPieces.add(pieceId);
      accepted.push(product);
    });
    return { products: accepted, duplicate_keys: duplicates };
  };

  const priorityValue = (product, priority) => {
    const labels = labelsFor(product);
    if (priority === 'newest' || priority === 'recently_released') {
      return timestampFor(product, priority);
    }
    if (priority === 'statement') {
      return familyFor(product) === 'statement_piece' || labels.includes('statement') ? 1 : 0;
    }
    if (priority === 'wave') return product?.release?.wave ? 1 : 0;
    return labels.includes(priority) ? 1 : 0;
  };

  const rankProducts = (products, priorities = []) => [...products].sort((left, right) => {
    for (const priority of priorities) {
      const difference = priorityValue(right, priority) - priorityValue(left, priority);
      if (difference) return difference;
    }
    return identityKey(left).localeCompare(identityKey(right));
  });

  const balanceFamilies = (products, policy = {}, requestedLimit = null) => {
    if (policy.enabled !== true) return [...products];
    const configured = list(policy.family_order).length > 0
      || Object.keys(policy.minimum_by_family || {}).length > 0
      || Object.keys(policy.maximum_by_family || {}).length > 0;
    if (!configured) return [...products];
    const groups = new Map();
    products.forEach((product) => {
      const family = familyFor(product) || 'unclassified';
      if (!groups.has(family)) groups.set(family, []);
      groups.get(family).push(product);
    });
    const order = unique([
      ...list(policy.family_order),
      ...groups.keys()
    ]).filter((family) => groups.has(family));
    const output = [];
    const used = new Set();
    const maximum = (family) => {
      const configured = Number(policy.maximum_by_family?.[family]);
      return Number.isInteger(configured) && configured >= 0 ? configured : Infinity;
    };
    const take = (family) => {
      const group = groups.get(family) || [];
      const candidate = group.find((product) => !used.has(identityKey(product)));
      const familyCount = output.filter((product) => familyFor(product) === family).length;
      if (!candidate || familyCount >= maximum(family)) return false;
      used.add(identityKey(candidate));
      output.push(candidate);
      return true;
    };

    order.forEach((family) => {
      const minimum = Math.max(0, Number(policy.minimum_by_family?.[family]) || 0);
      for (let index = 0; index < minimum; index += 1) {
        if (!take(family)) break;
      }
    });

    let progressed = true;
    while (progressed && (!requestedLimit || output.length < requestedLimit)) {
      progressed = false;
      order.forEach((family) => {
        if (requestedLimit && output.length >= requestedLimit) return;
        if (take(family)) progressed = true;
      });
    }
    products.forEach((product) => {
      if (requestedLimit && output.length >= requestedLimit) return;
      if (!used.has(identityKey(product))) {
        used.add(identityKey(product));
        output.push(product);
      }
    });
    return output;
  };

  const familyBalanceReport = (products, policy = {}) => {
    const counts = list(products).reduce((summary, product) => {
      const family = familyFor(product) || 'unclassified';
      summary[family] = (summary[family] || 0) + 1;
      return summary;
    }, {});
    if (policy.enabled !== true) {
      return {
        evaluated: false,
        balanced: null,
        counts,
        deficits: {},
        excesses: {},
        reason_codes: ['FAMILY_BALANCING_DISABLED']
      };
    }
    const families = unique([
      ...Object.keys(policy.minimum_by_family || {}),
      ...Object.keys(policy.maximum_by_family || {}),
      ...Object.keys(counts)
    ]);
    const deficits = {};
    const excesses = {};
    families.forEach((family) => {
      const count = counts[family] || 0;
      const minimum = Math.max(0, Number(policy.minimum_by_family?.[family]) || 0);
      const maximumValue = Number(policy.maximum_by_family?.[family]);
      const maximum = Number.isFinite(maximumValue) && maximumValue >= 0
        ? maximumValue
        : Infinity;
      if (count < minimum) deficits[family] = minimum - count;
      if (count > maximum) excesses[family] = count - maximum;
    });
    const configured = Object.keys(policy.minimum_by_family || {}).length > 0
      || Object.keys(policy.maximum_by_family || {}).length > 0;
    return {
      evaluated: configured,
      balanced: configured ? !Object.keys(deficits).length && !Object.keys(excesses).length : null,
      counts,
      deficits,
      excesses,
      reason_codes: configured
        ? (!Object.keys(deficits).length && !Object.keys(excesses).length
            ? ['FAMILY_BALANCED']
            : ['FAMILY_IMBALANCE'])
        : ['FAMILY_POLICY_NOT_CONFIGURED']
    };
  };

  const selectInventory = (products, rawQuery = {}, engine = ruleEngine) => {
    const query = normalizeQuery(rawQuery);
    const eligibility = evaluateEligibility(products, engine);
    const matching = eligibility.decisions
      .filter((decision) => decision.eligible)
      .map((decision) => decision.product)
      .filter((product) => !query.family || familyFor(product) === query.family)
      .filter((product) => !excludedByQuery(product, query));
    const deduped = deduplicate(matching);
    const ranked = rankProducts(deduped.products, query.priorities);
    const balanced = balanceFamilies(ranked, query.balance, query.limit);
    return {
      contract: contractFor(query.surface),
      query,
      catalog_generation: null,
      catalog_generation_source: 'not_connected',
      eligible_count: balanced.length,
      items: query.limit ? balanced.slice(0, query.limit) : balanced,
      eligibility: eligibility.decisions.map((decision) => ({
        subject: decision.subject,
        eligible: decision.eligible,
        reason_codes: decision.reason_codes
      })),
      duplicate_keys: deduped.duplicate_keys,
      source: 'shopify_public_snapshot',
      limitations: [
        'C3 eligibility and catalog generation are not connected.',
        'Shopify remains authoritative for current inventory and checkout.'
      ]
    };
  };

  const railHealth = (input = {}) => {
    const eligible = Math.max(0, Number(input.eligible_count) || 0);
    const current = Math.max(0, Number(input.current_count) || 0);
    const requested = Math.max(0, Number(input.requested_count) || 0);
    const refreshing = input.refreshing === true;
    const failed = input.failed === true;
    const target = requested > 0 ? Math.min(requested, eligible) : eligible;
    const needsRefresh = failed || input.stale === true || current < target;
    const health = refreshing
      ? 'REFRESHING'
      : eligible === 0
        ? 'DEPLETED'
        : failed
          ? 'STALE_FALLBACK'
          : needsRefresh
            ? 'NEEDS_REFRESH'
            : 'HEALTHY';
    return {
      eligible_count: eligible,
      current_count: current,
      requested_count: requested || null,
      health,
      needs_refresh: needsRefresh,
      observed_at: input.observed_at || new Date().toISOString()
    };
  };

  const replacementRequest = (input = {}) => {
    const reason = REPLACEMENT_REASONS.includes(input.reason)
      ? input.reason
      : 'REPLACE_EMPTY_RAIL';
    return {
      contract_version: CONTRACT_VERSION,
      request_id: publicId(input.request_id || [
        reason,
        input.surface_id,
        input.missing_product_id,
        input.catalog_generation
      ].join(':')),
      provider_status: 'architecture_only',
      reason,
      surface: SURFACES.includes(input.surface) ? input.surface : 'home_rail',
      surface_id: publicId(input.surface_id),
      family: normalizedToken(input.family),
      merchandising_role: normalizedToken(input.merchandising_role),
      missing_product_id: publicId(input.missing_product_id),
      catalog_generation: Number.isInteger(Number(input.catalog_generation))
        ? Number(input.catalog_generation)
        : null,
      requirements: {
        current_eligible_inventory: true,
        central_rule_engine: true,
        shopify_revalidation: true,
        no_future_wave_payload: true,
        no_live_publication: true
      },
      replacement: null
    };
  };

  const createEvent = (name, payload = {}, context = {}) => {
    if (!Object.values(EVENT_NAMES).includes(name)) return null;
    const occurredAt = context.occurred_at || new Date().toISOString();
    const surfaceId = publicId(payload.surface_id);
    return {
      event_id: publicId(context.event_id || `${name}:${surfaceId}:${payload.catalog_generation ?? 'none'}:${occurredAt}`),
      event_name: name,
      event_version: '1.0',
      occurred_at: occurredAt,
      producer: 'corkopia_dynamic_inventory',
      trace_id: publicId(context.trace_id || payload.request_id || `${name}:${surfaceId}`),
      classification: 'public_analytics',
      idempotency_key: publicId(context.idempotency_key || [
        name,
        surfaceId,
        payload.catalog_generation ?? 'none',
        payload.change_id || payload.request_id || occurredAt
      ].join(':')),
      payload: {
        surface: SURFACES.includes(payload.surface) ? payload.surface : 'home_rail',
        surface_id: surfaceId,
        family: normalizedToken(payload.family),
        eligible_count: Number.isFinite(Number(payload.eligible_count))
          ? Number(payload.eligible_count)
          : null,
        current_count: Number.isFinite(Number(payload.current_count))
          ? Number(payload.current_count)
          : null,
        health: publicId(payload.health),
        needs_refresh: payload.needs_refresh === true,
        catalog_generation: Number.isInteger(Number(payload.catalog_generation))
          ? Number(payload.catalog_generation)
          : null,
        reason_code: publicId(payload.reason_code)
      }
    };
  };

  const dispatchEvent = (event, target = root?.document) => {
    if (!event || !target?.dispatchEvent) return false;
    const EventConstructor = target.defaultView?.CustomEvent || root?.CustomEvent;
    const customEvent = EventConstructor
      ? new EventConstructor(EVENT_CHANNEL, { detail: event })
      : { type: EVENT_CHANNEL, detail: event };
    target.dispatchEvent(customEvent);
    return true;
  };

  return Object.freeze({
    CONTRACT_VERSION,
    RULE_SET_ID,
    RULE_SET_VERSION,
    PROVIDER_STATUS,
    C3_PROVIDER_STATUS,
    EVENT_CHANNEL,
    EVENT_NAMES,
    FUTURE_C3_STATES,
    MERCHANDISING_PRIORITIES,
    REPLACEMENT_REASONS,
    SURFACES,
    balanceFamilies,
    contractFor,
    createEvent,
    deduplicate,
    dispatchEvent,
    evaluateEligibility,
    familyBalanceReport,
    familyFor,
    identityKey,
    labelsFor,
    normalizeQuery,
    parseBoundMap,
    rankProducts,
    railHealth,
    replacementRequest,
    selectInventory
  });
});
